import React, { useMemo } from 'react';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import {
  LayoutDashboard, ClipboardList, Users, Menu as MenuIcon,
  Settings, Shield, Sliders, Building2, FolderKanban, ChevronRight,
} from 'lucide-react';

const ICON_MAP = {
  LayoutDashboard,
  ClipboardList,
  Users,
  Menu: MenuIcon,
  Settings,
  Shield,
  Sliders,
  Building2,
  FolderKanban,
};
const ACTIONS = ['ver', 'crear', 'editar', 'eliminar'];

/**
 * Tabla de permisos por menú con soporte para árbol de N niveles.
 * - Recursivo: indenta por `level`, ordena por `orden` dentro de cada nivel.
 * - Bulk toggle por fila padre: "Marcar todos" / "Limpiar" sobre los
 *   descendientes (todas las acciones).
 * - Contador "X/Y activos" en cada padre = descendientes con al menos un
 *   permiso activo / total de descendientes.
 *
 * Props:
 * - menus: array de menús con {id, nombre, padre_id, orden, icono}
 * - permisos: array de permisos del rol [{menu_id, ver, crear, editar, eliminar}]
 * - onChange: callback que recibe la nueva lista completa de permisos.
 * - readOnly: si true, deshabilita interacción (sin permitir cambios).
 * - allChecked: si true, muestra TODOS los checkboxes marcados y el contador
 *   completo (usado para el rol Administrador con bypass total de permisos).
 */
const RolPermissionsTable = ({ menus, permisos, onChange, readOnly = false, allChecked = false }) => {
  // Construye el árbol y devuelve lista plana con level, isParent, descIds.
  const organized = useMemo(() => {
    if (!menus || menus.length === 0) return [];

    // Index por padre
    const byParent = menus.reduce((acc, m) => {
      const key = m.padre_id || '__root__';
      (acc[key] ||= []).push(m);
      return acc;
    }, {});
    Object.values(byParent).forEach((arr) =>
      arr.sort((a, b) => (a.orden || 0) - (b.orden || 0))
    );

    // Recoge todos los IDs descendientes (no incluye el propio padre)
    const collectDescIds = (id) => {
      const out = [];
      const stack = [...(byParent[id] || [])];
      while (stack.length) {
        const cur = stack.shift();
        out.push(cur.id);
        if (byParent[cur.id]) stack.push(...byParent[cur.id]);
      }
      return out;
    };

    const out = [];
    const walk = (parentKey, level) => {
      (byParent[parentKey] || []).forEach((m) => {
        const descIds = byParent[m.id] ? collectDescIds(m.id) : [];
        out.push({ ...m, level, isParent: descIds.length > 0, descIds });
        walk(m.id, level + 1);
      });
    };
    walk('__root__', 0);
    return out;
  }, [menus]);

  const getP = (menuId, action) => {
    const p = (permisos || []).find((x) => x.menu_id === menuId);
    return p ? !!p[action] : false;
  };

  const setP = (menuId, action) => {
    const next = [...(permisos || [])];
    const i = next.findIndex((p) => p.menu_id === menuId);
    if (i >= 0) {
      next[i] = { ...next[i], [action]: !next[i][action] };
    } else {
      next.push({
        menu_id: menuId,
        ver: action === 'ver',
        crear: action === 'crear',
        editar: action === 'editar',
        eliminar: action === 'eliminar',
      });
    }
    onChange(next);
  };

  // Marca o desmarca TODAS las acciones de TODOS los descendientes.
  const bulkSetDescendants = (descIds, enable) => {
    const set = new Set(descIds);
    const filtered = (permisos || []).filter((p) => !set.has(p.menu_id));
    if (enable) {
      descIds.forEach((id) => {
        filtered.push({
          menu_id: id, ver: true, crear: true, editar: true, eliminar: true,
        });
      });
    }
    onChange(filtered);
  };

  // Conteo de descendientes con al menos un permiso activo
  const countActiveDesc = (descIds) => {
    if (allChecked) return descIds.length;
    return descIds.filter((id) => {
      const p = (permisos || []).find((x) => x.menu_id === id);
      return p && (p.ver || p.crear || p.editar || p.eliminar);
    }).length;
  };

  return (
    <div className="border border-[#E8E8E7] rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-[#F5F5F5]">
          <tr>
            <th className="text-left p-3 text-sm font-semibold text-[#2D2D2D]">Menú</th>
            {ACTIONS.map((a) => (
              <th key={a} className="text-center p-3 text-sm font-semibold text-[#2D2D2D] w-20 capitalize">
                {a}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {organized.map((menu) => {
            const Icon = ICON_MAP[menu.icono] || LayoutDashboard;
            const activeDesc = menu.isParent ? countActiveDesc(menu.descIds) : 0;
            return (
              <tr
                key={menu.id}
                className={`border-t border-[#E8E8E7] hover:bg-[#F5F5F5]/50 ${
                  menu.level === 0 && menu.isParent ? 'bg-[#FAFAFA]' : ''
                }`}
              >
                <td
                  className="p-3"
                  style={{ paddingLeft: `${12 + menu.level * 24}px` }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {menu.level >= 1 && (
                      <ChevronRight size={14} className="text-[#888888] shrink-0" />
                    )}
                    <Icon
                      size={menu.level === 0 ? 18 : 16}
                      className={menu.level === 0 ? 'text-[#9d8272]' : 'text-[#888888]'}
                    />
                    <span
                      className={`text-sm ${
                        menu.level === 0
                          ? 'font-semibold text-[#2D2D2D]'
                          : menu.isParent
                            ? 'font-medium text-[#3A3A3A]'
                            : 'text-[#5A5A5A]'
                      }`}
                    >
                      {menu.nombre}
                    </span>
                    {menu.isParent && (
                      <span
                        className="text-[10px] text-[#8a7864] bg-[#fbf7f0] border border-[#e8dfd0] px-1.5 py-0.5 rounded-full"
                        data-testid={`bulk-count-${menu.id}`}
                      >
                        {activeDesc}/{menu.descIds.length} activos
                      </span>
                    )}
                    {menu.isParent && !readOnly && (
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-[#9d8272] hover:bg-[#fbf7f0]"
                          onClick={() => bulkSetDescendants(menu.descIds, true)}
                          data-testid={`bulk-enable-${menu.id}`}
                        >
                          Marcar todos
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-[#888888] hover:bg-[#F5F5F5]"
                          onClick={() => bulkSetDescendants(menu.descIds, false)}
                          data-testid={`bulk-clear-${menu.id}`}
                        >
                          Limpiar
                        </Button>
                      </div>
                    )}
                  </div>
                </td>
                {ACTIONS.map((action) => (
                  <td key={action} className="p-3 text-center">
                    <Checkbox
                      checked={allChecked ? true : getP(menu.id, action)}
                      onCheckedChange={() => !readOnly && setP(menu.id, action)}
                      className="border-[#c5b8a2] data-[state=checked]:bg-[#9d8272] data-[state=checked]:border-[#9d8272]"
                      disabled={readOnly}
                      data-testid={`permission-${menu.id}-${action}`}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RolPermissionsTable;
